import { useQuery } from '@tanstack/react-query';
import type { Matcher } from 'react-day-picker';

import { Calendar } from '@nesso/ui/primitives/calendar';

import { scheduledSessionsOptions } from '@/entities/session';
import { formatLocalDate } from '@/shared/lib/time-utils';

import { getDayButtonLabel } from '../../lib/get-day-button-label';
import { getSessionDays } from '../../lib/get-session-days';
import { getVisibleMonthRange } from '../../lib/get-visible-month-range';
import { useMonthSwipe } from '../../lib/use-month-swipe';

type Props = {
    selectedDay: Date;
    shownMonth: Date;
    today: Date;
    selectDay: (day: Date) => void;
    showNextMonth: () => void;
    showPreviousMonth: () => void;
    showMonth: (month: Date) => void;
};

export function CalendarPageDatePicker({
    selectedDay,
    shownMonth,
    today,
    selectDay,
    showNextMonth,
    showPreviousMonth,
    showMonth,
}: Props) {
    const swipeHandlers = useMonthSwipe({ onNext: showNextMonth, onPrevious: showPreviousMonth });
    // A month that never loaded shows no dots; one that loaded before and then failed to refetch
    // keeps its last-known dots, since TanStack Query preserves `data` across a failed refetch.
    const { data } = useQuery(scheduledSessionsOptions(getVisibleMonthRange(shownMonth)));
    const sessionDays = getSessionDays(data?.sessions ?? []);
    const marked: Matcher = (day) => sessionDays.has(formatLocalDate(day));

    return (
        // `touch-pan-y` keeps a vertical pan free for scrolling while a horizontal one drives the
        // swipe handlers below.
        <div
            data-slot="calendar-page-date-picker"
            className="touch-pan-y border-b border-border bg-card"
            {...swipeHandlers}
        >
            <Calendar
                className="w-full p-3"
                cellSize="lg"
                dayStyle="accent"
                animate
                mode="single"
                required
                selected={selectedDay}
                onSelect={selectDay}
                month={shownMonth}
                today={today}
                onMonthChange={showMonth}
                hideNavigation
                hideCaption
                marked={marked}
                labels={{ labelDayButton: getDayButtonLabel }}
            />
        </div>
    );
}
